import {
  faCheckCircle,
  faCreditCard,
  faInfoCircle,
  faQuestionCircle,
  faSave,
  faXmarkCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { FormikHelpers, FormikProps, useFormikContext } from 'formik';
import {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import ModalInner, { type IModalStandard } from './modal-inner';
import { MODAL_TRANSITION_TIMEOUT, modalDropInSx } from './modal-transition';
import {
  ModalAnalyticsEventName,
  ModalAnalyticsParams,
  useModalAnalyticsEmitter,
} from './modal-analytics';
import {
  setOverlayChromePrimed,
  useThemeColorOverlay,
} from '../../hooks/use-theme-color/use-theme-color';
import type { ButtonProps } from '../button/button';
import { Form, type FormProps, type FormValues } from '../form/form';
import {
  Box,
  Dialog,
  DialogContent,
  DialogProps,
  Fade,
  IconButton,
  PaperProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, Download } from '@mui/icons-material';

export type TField =
  | string
  | number
  | boolean
  | Date
  | undefined
  | object
  | File;

export interface IModalProperties {
  headerClassNames: string[];
  icon?: FontAwesomeIconProps['icon'];
  muiIcon?: ReactNode;
  isLoading: boolean;
  buttons: ButtonProps[];
  onClose?: () => void;
  hideHeader?: boolean;
  /**
   * True while the modal refuses to close — a save/confirm is in flight, or the
   * body has called `setBusy(true)`. The header's close button disables on it;
   * a custom footer should disable its own Cancel the same way.
   */
  closeBlocked?: boolean;
  /**
   * Tell the modal that work it cannot see is in flight.
   *
   * The renderer already knows about its own `onSubmit` / `onConfirm`: while
   * one is pending, the close button, the backdrop and Escape do nothing. A
   * body that runs its own mutation (an `info` modal wrapping a form, a
   * confirm with a pre-flight query) is invisible to it, so the dialog could be
   * dismissed mid-request and the request would carry on with no UI. Call
   * `setBusy(true)` before that work and `setBusy(false)` after it, and the
   * same guard applies. It is released synchronously, so
   * `setBusy(false); onClose();` closes.
   */
  setBusy?: (busy: boolean) => void;
  /**
   * For a body that does its own saving and then calls `onClose`: call this
   * once that work succeeds (before closing) so a named modal reports
   * `modal_submit` instead of `modal_dismiss`. The renderer's own
   * `onSubmit` / `onConfirm` paths report it already.
   */
  markSubmitted?: () => void;
  /**
   * Id of the element holding the modal's title, which the dialog is labelled
   * by. Absent when the header is hidden.
   */
  titleId?: string;
}

export interface IModalFooterProps<TForm = Record<string, unknown>> {
  formikProps?: FormikProps<TForm>;
  modalProps: IModalProperties;
}

interface IModalBase {
  type: 'info' | 'ask' | 'save' | 'payment' | 'readOnly';
  title: string | ReactNode;
  icon?: FontAwesomeIconProps['icon'];
  iconClassName?: string;
  muiIcon?: ReactNode;
  /** Force fullscreen at every breakpoint. */
  fullscreen?: string | true;
  /**
   * Go fullscreen only on small screens (< sm breakpoint), staying a normal
   * centered dialog on larger screens.
   *
   * Defaults ON for every type that carries real content — `save`, `readOnly`,
   * `payment` and `info` — because a centered card on a phone spends most of
   * its width on backdrop and dialog margin, and the content inside is what
   * needs the room. `info` is in that list on purpose: it is the catch-all for
   * anything that isn't a Formik form (global search, action lists, pickers,
   * forms that own their own Formik), not just one-line messages.
   *
   * Defaults OFF for `ask` only, which is a sentence and two buttons; a full
   * screen of empty space around one line reads as a page, not a prompt.
   *
   * Pass it explicitly to override either default — `false` keeps a modal
   * centered (do this for an `info` that really is a short message or a
   * confirm), `true` takes an `ask` fullscreen.
   */
  fullscreenOnMobile?: boolean;
  isLoading?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  error?: string;
  // @deprecated
  backdrop?: string | boolean;
  disableBackdropClick?: boolean;
  backdropClassName?: string;
  centered?: boolean;
  onCancel?: () => void;
  dialogProps?: Partial<DialogProps>;
  hideHeader?: boolean;
  /**
   * Funnel name (snake_case). When set, the modal reports `modal_open`, then
   * `modal_submit` once a save/confirm resolves or `modal_dismiss` when it is
   * closed any other way. Unnamed modals report nothing.
   */
  analyticsName?: string;
  /** Extra dimensions on this modal's events — see IMultiStepModal. */
  analyticsContext?: ModalAnalyticsParams;
}

interface IModalInfo {
  type: 'info';
  okText?: string;
  okIcon?: FontAwesomeIconProps['icon'];
  body: ReactNode | ((ModalProps: IModalProperties) => ReactNode);
  footer?: ReactNode;
}

interface IModalAsk {
  type: 'ask';
  variant?: string;
  yesDisabled?: boolean;
  yesText?: string;
  yesIcon?: FontAwesomeIconProps['icon'];
  noText?: string;
  noIcon?: FontAwesomeIconProps['icon'];
  body: ReactNode;
  footer?:
    | ReactNode
    | (({ formikProps, modalProps }: IModalFooterProps) => ReactNode);
  onConfirm?: (() => void) | (() => Promise<void>);
}

interface IModalSave<TForm extends FormValues<TForm>> {
  type: 'save';
  saveText?: string;
  /** `null` shows the save button with no icon. */
  saveIcon?: FontAwesomeIconProps['icon'] | null;
  cancelText?: string;
  cancelIcon?: FontAwesomeIconProps['icon'];
  initialValues: FormProps<TForm>['initialValues'];
  body: ReactNode | ((formikProps: FormikProps<TForm>) => ReactNode);
  footer?:
    | ReactNode
    | (({ formikProps, modalProps }: IModalFooterProps<TForm>) => ReactNode);
  validate?: FormProps<TForm>['validate'];
  onSubmit?:
    | ((values: TForm, formikHelpers: FormikHelpers<TForm>) => void)
    | ((values: TForm, formikHelpers: FormikHelpers<TForm>) => Promise<void>);
}

interface IModalReadOnly<TForm extends FormValues<TForm>> {
  type: 'readOnly';
  okText?: string;
  okIcon?: FontAwesomeIconProps['icon'];
  initialValues: FormProps<TForm>['initialValues'];
  body: ReactNode | ((formikProps: FormikProps<TForm>) => ReactNode);
  footer?:
    | ReactNode
    | (({ formikProps, modalProps }: IModalFooterProps<TForm>) => ReactNode);
}

interface IModalPayment {
  type: 'payment';
  body: ReactNode;
  footer: null;
}

/** Displays an image or PDF at full size with no chrome other than a close button. */
export interface IModalImagePreview {
  type: 'imagePreview';
  /** The URL of the image or PDF to display. */
  src: string;
  /** Accessible alt text for the image. */
  alt?: string;
  /** Optional mime type — used to decide whether to render an iframe (e.g. 'application/pdf'). */
  mimeType?: string;
}

export type IModal<
  TForm extends FormValues<TForm> = Partial<Record<string, TField>>,
> =
  | (IModalBase &
      (
        | IModalInfo
        | IModalAsk
        | IModalSave<TForm>
        | IModalPayment
        | IModalReadOnly<TForm>
      ))
  | IModalImagePreview;

/**
 * Modal types that keep their centered-card shape on a phone.
 *
 * Only `ask`: a confirm is a short question and two buttons, and blowing that
 * up to fill a phone reads as a page the user navigated to rather than a
 * prompt.
 *
 * `info` was here too, on the theory that it is the same shape — but in
 * practice it is the catch-all for content that isn't a Formik form: global
 * search, the calendar's action list, pickers. Those are full surfaces, and
 * centring them wasted most of the screen. An `info` that really is one
 * sentence can opt out with `fullscreenOnMobile: false`.
 */
const CENTERED_ON_MOBILE_TYPES = new Set<IModalBase['type']>(['ask']);

export interface IModalRendererProps<TForm extends FormValues<TForm>> {
  modal: IModal<TForm>;
  onClose?: () => void;
}

/**
 * A Formik modal's body, reading the form from the context the dialog paper
 * provides. Defined at module scope: a component created inside the renderer
 * would be a new type on every render, remounting the body — and wiping its
 * state — whenever the renderer re-rendered (loading, an error, a breakpoint).
 * @param props - Component props
 * @param props.modal - The modal being shown
 * @param props.modalProps - Header/buttons/loading state for the modal
 * @param props.fullScreen - Whether the dialog is covering the whole screen
 * @returns The modal's inner content, with the Formik props
 */
function FormikModalBody<TForm extends FormValues<TForm>>({
  modal,
  modalProps,
  fullScreen,
}: {
  modal: IModalStandard<TForm>;
  modalProps: IModalProperties;
  fullScreen: boolean;
}) {
  const formikProps = useFormikContext<TForm>();
  return (
    <ModalInner
      modal={modal}
      modalProps={modalProps}
      formikProps={formikProps}
      fullScreen={fullScreen}
    />
  );
}

export default function ModalRenderer<TForm extends FormValues<TForm>>({
  modal,
  onClose,
}: IModalRendererProps<TForm>) {
  const [show, setShow] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [busy, setBusyState] = useState(false);
  const titleId = useId();

  // What `handleClose` consults. Refs rather than the state above because the
  // close paths are captured in closures the body holds on to (`modalProps`
  // handed to an `info` body function), and because "release, then close" has
  // to work within one tick: `setBusy(false); onClose()`.
  const loadingRef = useRef(false);
  const busyRef = useRef(false);

  // Funnel analytics. Keyed on the modal object, not on mount: the provider
  // reuses one renderer for consecutive modals of the same type. The ref also
  // stops StrictMode's double effect from reporting one open twice. Every open
  // gets exactly one ending — including when it is replaced in place or the
  // renderer unmounts (`destroy()`, a modal of another type) — so the params
  // are captured at open time, for endings that outlive the render.
  const emitAnalytics = useModalAnalyticsEmitter();
  const emitAnalyticsRef = useRef(emitAnalytics);
  emitAnalyticsRef.current = emitAnalytics;
  const openedModalRef = useRef<{
    modal: unknown;
    params?: ModalAnalyticsParams;
  } | null>(null);
  const modalOutcomeRef = useRef<'submit' | 'dismiss' | null>(null);
  const endModal = useCallback(
    (outcome: 'submit' | 'dismiss', extra?: ModalAnalyticsParams) => {
      const opened = openedModalRef.current;
      if (!opened || modalOutcomeRef.current) return;
      modalOutcomeRef.current = outcome;
      if (!opened.params) return;
      const name: ModalAnalyticsEventName =
        outcome === 'submit' ? 'modal_submit' : 'modal_dismiss';
      emitAnalyticsRef.current(name, { ...opened.params, ...extra });
    },
    [],
  );
  useEffect(() => {
    if (openedModalRef.current?.modal === modal) return;
    // A modal of the same type opened over this one, in this renderer.
    endModal('dismiss', { reason: 'replaced' });
    const analyticsName =
      modal.type === 'imagePreview' ? undefined : modal.analyticsName;
    const params = analyticsName
      ? {
          modal: analyticsName,
          modal_type: modal.type,
          ...(modal.type === 'imagePreview' ? {} : modal.analyticsContext),
        }
      : undefined;
    openedModalRef.current = { modal, params };
    modalOutcomeRef.current = null;
    if (params) emitAnalyticsRef.current('modal_open', params);
  }, [modal, endModal]);
  // Deferred, and cancelled by a remount, so StrictMode's rehearsal unmount
  // doesn't end the open.
  const unmountTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  useEffect(() => {
    clearTimeout(unmountTimerRef.current);
    return () => {
      unmountTimerRef.current = setTimeout(
        () => endModal('dismiss', { reason: 'unmount' }),
        0,
      );
    };
  }, [endModal]);
  const markSubmitted = useCallback(() => endModal('submit'), [endModal]);

  const setLoading = useCallback((value: boolean) => {
    loadingRef.current = value;
    setIsLoading(value);
  }, []);

  const setBusy = useCallback((value: boolean) => {
    busyRef.current = value;
    setBusyState(value);
  }, []);

  const muiTheme = useTheme();
  const isMobileScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const fullScreen =
    modal.type !== 'imagePreview' &&
    (!!modal.fullscreen ||
      ((modal.fullscreenOnMobile ??
        !CENTERED_ON_MOBILE_TYPES.has(modal.type)) &&
        isMobileScreen));

  // Register as an open overlay for as long as we're showing — that presence is
  // what drives `color-scheme: dark` on Safari's own toolbar, so it must be true
  // for EVERY modal, not just the ones that name a colour.
  //
  // Only a fullscreen dialog names one, because only it covers the app header;
  // otherwise the strip follows the header's colour automatically. Above the
  // imagePreview early return — that's a dialog too.
  //
  // A preview on a phone is fullscreen as well, under its own dark toolbar.
  const previewFullScreen = modal.type === 'imagePreview' && isMobileScreen;
  const { grey, background } = muiTheme.palette;
  let overlayColor: string | undefined;
  if (previewFullScreen) overlayColor = grey['900'];
  else if (fullScreen) overlayColor = background.paper;
  useThemeColorOverlay(show, overlayColor);

  // Hold the chrome dim for as long as we're showing; release on close/unmount.
  //
  // The provider primes it just before mounting us so Safari recolours while the
  // page can still scroll, but it can't un-prime: `handleClose` only flips
  // `show`, it never calls the parent's onClose (only the imagePreview branch
  // does), so the provider still believes a modal is open. Hence tracking it
  // here, from the thing that actually knows.
  //
  // Must SET the value, not just clear it — StrictMode runs mount → cleanup →
  // mount, so a cleanup-only release cancels the priming the moment we appear
  // and never restores it.
  useEffect(() => {
    setOverlayChromePrimed(show);
    return () => setOverlayChromePrimed(false);
  }, [show]);

  // Image preview modals render their own minimal UI — bypass all standard chrome.
  if (modal.type === 'imagePreview') {
    const handleClose = () => {
      setShow(false);
      // Notify the provider so it clears the modal from context. This ensures
      // the next preview() call triggers a fresh mount with show=true.
      onClose?.();
    };
    const isPdf = modal.mimeType === 'application/pdf';
    // The dark bar across the top: download and close, plus the file's name
    // when the preview fills the screen and there is room to say what it is.
    const toolbar = (
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          bgcolor: 'grey.900',
          // Fullscreen owns the viewport, so the bar has to clear the notch.
          ...(previewFullScreen && {
            pt: 'calc(env(safe-area-inset-top) + 4px)',
          }),
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{ flex: 1, minWidth: 0, pl: 0.5, color: 'white' }}
        >
          {previewFullScreen ? modal.alt : null}
        </Typography>
        <IconButton
          // A phone needs the larger target; the bar is all the chrome there is.
          size={previewFullScreen ? 'medium' : 'small'}
          component="a"
          href={modal.src}
          download={modal.alt ?? true}
          aria-label="Download file"
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <Download fontSize="small" />
        </IconButton>
        <IconButton
          size={previewFullScreen ? 'medium' : 'small'}
          onClick={handleClose}
          aria-label="Close preview"
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );

    // On a phone the preview takes the whole screen. As a centred card it was
    // 80vw of a 390px screen with a dimmed margin all round — the document
    // too small to read, and the margin the only thing to tap to get out.
    if (previewFullScreen) {
      return (
        <Dialog
          open={show}
          onClose={handleClose}
          fullScreen
          slots={{ transition: Fade }}
          slotProps={{
            transition: { timeout: MODAL_TRANSITION_TIMEOUT },
            paper: { sx: { bgcolor: 'common.black' } },
          }}
        >
          {toolbar}
          {isPdf ? (
            <Box
              component="iframe"
              src={modal.src}
              title={modal.alt ?? 'PDF Preview'}
              sx={{
                display: 'block',
                flex: 1,
                minHeight: 0,
                width: '100%',
                border: 'none',
                bgcolor: 'common.white',
              }}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                // A tall image scrolls rather than shrinking to a sliver: it
                // is held to the screen's width only, never its height.
                overflow: 'auto',
                pb: 'env(safe-area-inset-bottom)',
              }}
            >
              <Box
                component="img"
                src={modal.src}
                alt={modal.alt ?? 'Preview'}
                sx={{
                  display: 'block',
                  // Centres an image that fits. Centring from the container
                  // instead would put the top of one that overflows out of
                  // reach above the scroll.
                  m: 'auto',
                  maxWidth: '100%',
                }}
              />
            </Box>
          )}
        </Dialog>
      );
    }

    return (
      <Dialog
        open={show}
        onClose={handleClose}
        maxWidth={false}
        // Quick fade in + a subtle 20px drop.
        slots={{ transition: Fade }}
        slotProps={{
          transition: { timeout: MODAL_TRANSITION_TIMEOUT },
          // The paper is sized to the image/PDF it holds (`maxWidth={false}`),
          // so the theme's full-bleed-on-mobile rule would stretch it into a
          // screen-sized sheet with the preview in one corner.
          paper: { sx: modalDropInSx, className: 'fs-dialog-compact' },
        }}
      >
        {/* maxWidth={false}: size the paper to the preview content. A fixed cap
            (e.g. "lg") is narrower than the 80vw iframe below on wide screens,
            pushing the PDF out of the dialog and forcing sideways scrolling. */}
        <DialogContent sx={{ p: 0, position: 'relative', lineHeight: 0 }}>
          {isPdf ? (
            /* PDF: dedicated toolbar above the iframe so buttons don't overlap native PDF controls */
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {toolbar}
              <Box
                component="iframe"
                src={modal.src}
                title={modal.alt ?? 'PDF Preview'}
                sx={{
                  display: 'block',
                  width: '80vw',
                  height: '85vh',
                  border: 'none',
                }}
              />
            </Box>
          ) : (
            /* Image: floating buttons over the image */
            <>
              <IconButton
                size="small"
                component="a"
                href={modal.src}
                download={modal.alt ?? true}
                aria-label="Download file"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 48,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                  zIndex: 1,
                }}
              >
                <Download fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleClose}
                aria-label="Close preview"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,0.45)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                  zIndex: 1,
                }}
              >
                <Close fontSize="small" />
              </IconButton>
              <Box
                component="img"
                src={modal.src}
                alt={modal.alt ?? 'Preview'}
                sx={{
                  display: 'block',
                  maxWidth: '90vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  useEffect(() => {
    setShow(!!modal);
    // A new modal in the same renderer starts with nothing in flight.
    setBusy(false);
  }, [modal, setBusy]);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      if (modal?.type === 'ask' && modal?.onConfirm) {
        await modal.onConfirm();
      }
      endModal('submit');
      setShow(false);
    } catch (error) {
      modal.error = (error as Error).message;
    } finally {
      setLoading(false);
    }
  }, [modal, setLoading, endModal]);

  const handleSubmit = useCallback(
    async (values: TForm, formikHelpers: FormikHelpers<TForm>) => {
      // Only when there is something to wait for: with no `onSubmit` nothing
      // would ever clear the flag, and a modal that is "loading" can't close.
      if (modal?.type === 'save' && modal?.onSubmit) {
        setLoading(true);
        try {
          await modal.onSubmit(values, formikHelpers);
          endModal('submit');
          setShow(false);
        } catch (error) {
          modal.error = (error as Error).message;
        } finally {
          setLoading(false);
        }
      }
    },
    [modal, setLoading, endModal],
  );

  // Every dismissal — the header's close button, the backdrop, Escape, and the
  // default Cancel/Ok — comes through here, so this is the one place that has
  // to refuse while work is in flight. Closing mid-request doesn't cancel the
  // request; it just removes the only UI that says it is happening (and, for
  // an upload, invites doing it again).
  const handleClose = useCallback(() => {
    if (loadingRef.current || busyRef.current) return;
    endModal('dismiss');
    setShow(false);
    if (modal?.onCancel) {
      modal.onCancel();
    }
  }, [modal, endModal]);

  const baseModalProps: IModalProperties = useMemo(() => {
    switch (modal.type) {
      case 'info':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faInfoCircle,
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [
            {
              content: modal.okText || 'Ok',
              icon: modal.okIcon || faCheckCircle,
              variant: 'primary',
              onClick: handleClose,
            },
          ],
        };
      case 'ask':
        return {
          headerClassNames: [`bg-${modal.variant || 'body-secondary'}`],
          icon: faQuestionCircle,
          iconClassName: modal.iconClassName || 'text-warning',
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [
            {
              content: modal.noText || 'Cancel',
              icon: modal.noIcon || faXmarkCircle,
              variant: 'ghost',
              onClick: handleClose,
            },
            {
              type: 'submit',
              content: modal.yesText || 'Yes',
              icon: modal.yesIcon || faCheckCircle,
              variant: 'primary',
              onClick: handleConfirm,
              disabled: modal.yesDisabled,
              isLoading,
            },
          ],
        };
      case 'payment':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faCreditCard,
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [],
        };
      case 'save':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faSave,
          onClose: handleClose,
          isLoading,
          hideHeader: modal.hideHeader,
          buttons: [
            {
              content: modal.cancelText || 'Cancel',
              variant: 'ghost',
              onClick: handleClose,
              disabled: isLoading,
            },
            {
              type: 'submit',
              content: modal.saveText || 'Save',
              icon:
                modal.saveIcon === null ? undefined : modal.saveIcon || faSave,
              variant: 'primary',
              isLoading,
            },
          ],
        };
      case 'readOnly':
        return {
          headerClassNames: ['bg-body-secondary'],
          icon: faSave,
          onClose: handleClose,
          isLoading,
          buttons: [
            {
              content: modal.okText || 'Ok',
              icon: modal.okIcon || faCheckCircle,
              variant: 'primary',
              onClick: handleClose,
            },
          ],
        };
      default:
        return {
          headerClassNames: [],
          icon: faInfoCircle,
          isLoading,
          buttons: [],
        };
    }
  }, [handleClose, handleConfirm, isLoading, modal]);

  const modalProps: IModalProperties = useMemo(
    () => ({
      ...baseModalProps,
      closeBlocked: isLoading || busy,
      setBusy,
      markSubmitted,
      titleId: baseModalProps.hideHeader ? undefined : titleId,
    }),
    [baseModalProps, isLoading, busy, setBusy, markSubmitted, titleId],
  );

  const dialogPaperComponentProps = useMemo(() => {
    switch (modal.type) {
      case 'save':
        return {
          component: Form,
          onSubmit: handleSubmit,
          initialValues: modal.initialValues,
          validate: modal.validate,
        };

      case 'readOnly':
        return {
          component: Form,
          initialValues: modal.initialValues,
        };

      default:
        return {};
    }
  }, [modal.fullscreen, show, handleClose]);

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth={modal.size || 'md'}
      fullWidth
      // MUI puts this on the paper — the element with role="dialog" — so the
      // dialog is named by its own visible title. (An `aria-label` passed
      // through `dialogProps` lands on the presentation root instead, where
      // nothing reads it.)
      aria-labelledby={modalProps.titleId}
      // Quick fade in + a subtle 20px drop.
      slots={{ transition: Fade }}
      slotProps={{
        transition: { timeout: MODAL_TRANSITION_TIMEOUT },
        paper: {
          // Opt out of the theme's full-bleed-on-mobile rule: this renderer has
          // already decided, via `fullScreen`, whether this modal fills the
          // screen. Without it an `ask` confirm would be dragged fullscreen.
          className: fullScreen ? undefined : 'fs-dialog-compact',
          // No header means no title element to point at; name the dialog
          // directly when the title is plain text.
          ...(modal.hideHeader &&
            typeof modal.title === 'string' && { 'aria-label': modal.title }),
          ...dialogPaperComponentProps,
          sx: {
            ...modalDropInSx,
            ...(dialogPaperComponentProps as { sx?: object })?.sx,
          },
        } as unknown as PaperProps<'form', FormikProps<TForm>>,
      }}
      {...modal.dialogProps}
    >
      {modal.type === 'save' || modal.type === 'readOnly' ? (
        <FormikModalBody
          modal={modal}
          modalProps={modalProps}
          fullScreen={fullScreen}
        />
      ) : (
        <ModalInner
          modal={modal}
          modalProps={modalProps}
          fullScreen={fullScreen}
        />
      )}
    </Dialog>
  );
}
